import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Tabs, Tab } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Box from '@components/Atoms/Box';
import Button from '@components/Atoms/Button/Button';
import SectionWrapper from '@components/Atoms/SectionWrapper';
import Skeleton from '@components/Atoms/Skeleton';
import Typography from '@components/Atoms/Typography';
import { useReportsFeatureAccessQuery } from '@components/ReportGenerator/ReportQueries';
import { useCurrentSection } from '@reducers/sections/hooks';
import { ISection, ISectionSubject } from '@CT-Types/section';
import InformationCard from '@components/Component/InformationCard/InformationCard';
import { usePMOutcomeReport } from './hooks';
import PMOutcomeHierarchy from './PMOutcomeHierarchy';
import { buildSummaryColumns } from './columns';
import { PMOutcomeReport, PMSectionNode, PMStudentNode } from './types';

/**
 * Since the same api is used for the embedded reports and the full report page,
 * we cant do subject filtering in the api. Instead, we'll filter out subject reports
 * by looking into the section->subjects and mapping it out with the reports.subjects.
 */
const sectionSubjectToDataSubjectMap = {
  Geometry: 'Geometry',
  'Algebra 1': 'Algebra',
  'Algebra 1A': 'Algebra 1-A',
  'Algebra 1-A': 'Algebra 1-A',
};

/**
 * Resolve the scoped PM outcome report for one exam: probe the default
 * period to learn the period list, then (only when the exam belongs to a
 * different period) fetch that period. When the exam is not a configured PM
 * exam, data is null and embeds render nothing.
 */
export const usePMReportForExam = (examId: number | undefined) => {
  const enabled = useReportsFeatureAccessQuery('pm_reports');
  // No feature access (or no exam yet) means no request at all — the probe
  // must never fire for students or districts without PM reports
  const probeEnabled = !!enabled && examId !== undefined;
  const probe = usePMOutcomeReport(null, null, null, null, null, { enabled: probeEnabled });
  const targetEntry = probe.data?.periods.find((p) => p.exam_id === examId);
  // A configured period with no tally data yet is not viewable — treat it
  // like a non-PM exam so callers keep their default score views
  const targetPeriod = targetEntry?.has_data ? targetEntry.period : null;
  // Same query key as the probe when no refetch is needed, so react-query
  // dedupes instead of loading the report twice
  const needsRefetch = !!targetPeriod && probe.data?.period !== targetPeriod;
  const refetch = usePMOutcomeReport(null, null, needsRefetch ? targetPeriod : null, null, null, {
    enabled: probeEnabled,
  });

  const data: PMOutcomeReport | null =
    (targetPeriod && (probe.data?.period === targetPeriod ? probe.data : refetch.data?.period === targetPeriod ? refetch.data : null)) ||
    null;
  return {
    enabled,
    isLoading: enabled && (probe.isLoading || (needsRefetch && refetch.isLoading)),
    isPMExam: !!targetPeriod,
    data,
  };
};

const FullReportLink: React.FC = () => (
  <Button component={Link} to="/admin/reports/pm-outcomes" size="small" variant="outlined" endIcon={<OpenInNewIcon />}>
    Full report &amp; growth analysis
  </Button>
);

/**
 * Display a card when no scores are available yet
 */
const NoScoresCard: React.FC = () => (
  <InformationCard
    img="/images/report-generator.png"
    title="Scores aren't available yet"
    description={
      <>
        They&apos;ll show up here after students finish this test and results finish processing.
        <br />
        Refresh this page in a few minutes if you expect scores.
      </>
    }
    extraStyle={{
      width: '100%',
      height: '40vh',
    }}
  />
);

/**
 * Teacher-facing embed of the Progress Monitoring summary (current period
 * only — growth analysis lives on the full reports page, linked above the
 * table). Rendered on the test session reports page for PM exams; renders
 * nothing at all (including the section chrome) everywhere else.
 */
export const PMOutcomeEmbed: React.FC<{ examId: number | undefined }> = ({ examId }) => {
  const [subjectIndex, setSubjectIndex] = useState(0);
  const { enabled, isLoading, isPMExam, data } = usePMReportForExam(examId);
  const currentSection = useCurrentSection() as ISection;
  const sectionSubjectNames = currentSection?.subjects?.map(
    (subject: ISectionSubject) => sectionSubjectToDataSubjectMap[subject.display_name],
  ).filter(Boolean);

  if (isLoading) {
    return <Skeleton height="12rem" width="100%" />;
  }

  if (!enabled || !examId || !isPMExam || !sectionSubjectNames || sectionSubjectNames.length === 0) {
    return null;
  }

  if (!data || data.subjects.length === 0) {
    return <NoScoresCard />;
  }

  const { subjects } = data;
  const filteredSubjects = subjects.filter((subject) => sectionSubjectNames.includes(subject.subject));
  if (filteredSubjects.length === 0) {
    return <NoScoresCard />;
  }
  const active = filteredSubjects[Math.min(subjectIndex, filteredSubjects.length - 1)];

  return (
    <SectionWrapper
      title="Progress Monitoring Outcomes"
      description="Blooms-weighted reporting category scores for this progress monitoring exam. Growth analysis against earlier periods is on the full report page."
      uniqueTestId="pm-outcomes-section"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Tabs
            value={Math.min(subjectIndex, filteredSubjects.length - 1)}
            onChange={(_e, value) => setSubjectIndex(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: '2.25rem',
              '& .MuiTab-root': { minHeight: '2.25rem', py: 0.5 },
            }}
          >
            {filteredSubjects.map((subject) => (
              <Tab key={subject.subject} label={subject.subject} />
            ))}
          </Tabs>
          <FullReportLink />
        </Box>
        <PMOutcomeHierarchy
          data={active}
          districtName={data.district_name}
          bands={data.bands}
          leaves={buildSummaryColumns(active, data.period, null, data.bands, 'weighted')}
        />
      </Box>
    </SectionWrapper>
  );
};

/**
 * Per-student weighted PM scores for one class section — the properly
 * calculated (Blooms-weighted) scores for the Scores page, replacing the raw
 * score table for PM exams. One block per subject the section has results
 * in; weighted and raw shown side by side.
 */
export const PMSectionScores: React.FC<{
  examId: number | undefined;
  sectionId: number;
}> = ({ examId, sectionId }) => {
  const { enabled, isLoading, isPMExam, data } = usePMReportForExam(examId);
  const currentSection = useCurrentSection() as ISection;
  const sectionSubjectNames = currentSection?.subjects?.map(
    (subject: ISectionSubject) => sectionSubjectToDataSubjectMap[subject.display_name],
  );

  if (isLoading && !data) {
    return <Skeleton height="8rem" width="100%" />;
  }

  if (!enabled || !examId || !sectionSubjectNames || sectionSubjectNames.length === 0 || !isPMExam) {
    return null;
  }

  if (!data) {
    return <NoScoresCard />;
  }

  const blocks: Array<{ subject: string; section: PMSectionNode }> = [];
  data.subjects.forEach((subject) => {
    if (!sectionSubjectNames.includes(subject.subject)) {
      return;
    }
    subject.organizations.forEach((org) =>
      org.teachers.forEach((teacher) =>
        teacher.sections.forEach((section) => {
          if (section.section_id === sectionId) {
            blocks.push({ subject: subject.subject, section });
          }
        }),
      ),
    );
  });

  if (blocks.length === 0) {
    return <NoScoresCard />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
      {blocks.map(({ subject, section }) => (
        <Box key={`${subject}-${section.section_id}`}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            {subject}
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              class average {section.overall_pct != null ? `${section.overall_pct}%` : '—'}
            </Typography>
          </Typography>
          <Box
            component="table"
            sx={{
              width: '100%',
              borderCollapse: 'collapse',
              '& th, & td': {
                textAlign: 'left',
                p: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
              },
              '& th': { typography: 'caption', color: 'text.secondary' },
            }}
          >
            <thead>
              <tr>
                <th>Student</th>
                <th>Weighted Score</th>
                <th>Raw Correct</th>
              </tr>
            </thead>
            <tbody>
              {section.students.map((student: PMStudentNode) => (
                <tr key={student.student_id}>
                  <td>{student.student_name}</td>
                  <td>{student.overall_pct != null ? `${student.overall_pct}%` : '—'}</td>
                  <td>{student.raw_pct != null ? `${student.raw_pct}%` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default PMOutcomeEmbed;
